import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listExamConfigs, listExamResults } from "../../api/examApi";
import { listRemarksTemplates, saveExamRemarks } from "../../api/examRemarksApi";
import "../Attendance/Attendance.css";

/**
 * Direct port of digicampusbd's "Comments/Remark" (`ExamRemarksController#saveRemarks`,
 * was `exam-remarks-give`) — apply one remark (picked from a saved template, or typed
 * free-form) to several students' exam results at once. The student list is the same
 * `GET /api/exam-results` call the merit/fail-list reports already use (old app's own
 * `/exam/report/section/result/for/remarks` is `sectionResults` — the same "all
 * students, pass and fail" query `exam-report-section-result` already exercises).
 */
export default function ExamRemarksGivePage() {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [results, setResults] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [customRemark, setCustomRemark] = useState("");
  const [selectedResultIds, setSelectedResultIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
    });
    listRemarksTemplates().then(setTemplates);
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);
    if (!cc) return;
    listExamConfigs(cc.classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classConfigurationId, classConfigs]);

  useEffect(() => {
    if (!examConfigId || !classConfigurationId) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError("");
    let cancelled = false;
    listExamResults(examConfigId)
      .then((all) => {
        if (!cancelled) {
          setResults(all.filter((r) => String(r.classConfigurationId) === classConfigurationId));
        }
      })
      .catch(() => setError("Failed to load results."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [examConfigId, classConfigurationId]);

  const toggleRow = (resultId) => {
    setSelectedResultIds((prev) => (prev.includes(resultId) ? prev.filter((id) => id !== resultId) : [...prev, resultId]));
  };

  const toggleAll = (checked) => {
    setSelectedResultIds(checked ? results.map((r) => r.resultId) : []);
  };

  const effectiveRemark = templateId ? templates.find((t) => String(t.id) === templateId)?.remarks || "" : customRemark;

  const handleApply = async () => {
    setError("");
    setSuccess("");
    if (selectedResultIds.length === 0) {
      setError("Please select at least one student.");
      return;
    }
    if (!effectiveRemark.trim()) {
      setError("Please pick a remarks template or type a remark.");
      return;
    }
    setSaving(true);
    try {
      await saveExamRemarks({ remarks: effectiveRemark, resultIds: selectedResultIds });
      setSuccess(`Remark applied to ${selectedResultIds.length} student(s).`);
      setSelectedResultIds([]);
      const updated = await listExamResults(examConfigId);
      setResults(updated.filter((r) => String(r.classConfigurationId) === classConfigurationId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save remarks.");
    } finally {
      setSaving(false);
    }
  };

  const allChecked = results.length > 0 && results.every((r) => selectedResultIds.includes(r.resultId));

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Comments / Remark</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="attendance-filters">
        <label>
          Remarks Template
          <select
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              if (e.target.value) setCustomRemark("");
            }}
          >
            <option value="">— Type custom remark instead —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.remarksTitle}
              </option>
            ))}
          </select>
        </label>
        {!templateId && (
          <label style={{ flex: "1 1 260px" }}>
            Custom Remark
            <input value={customRemark} onChange={(e) => setCustomRemark(e.target.value)} placeholder="Type a remark" />
          </label>
        )}
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />
                </th>
                <th>Roll</th>
                <th>Student ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Current Remark</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.resultId}>
                  <td>
                    <input type="checkbox" checked={selectedResultIds.includes(r.resultId)} onChange={() => toggleRow(r.resultId)} />
                  </td>
                  <td>{r.studentRoll}</td>
                  <td>{r.customStudentId}</td>
                  <td>{r.studentName}</td>
                  <td>{r.resultStatus}</td>
                  <td>{r.remark || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && results.length === 0 && <p>No results found for this selection.</p>}

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleApply} disabled={saving || results.length === 0}>
          {saving ? "Applying..." : "Apply Remark"}
        </button>
      </div>
    </MainLayout>
  );
}
